/**
 * The two shapes a workflow input's `constraints.options` entry comes in — a bare string, or
 * `{ value, label }` — read in ONE place (MAR-948).
 *
 * The run form's chips, `WorkflowFieldControl`'s dropdown and `partyValues.ts`'s option matching each
 * spelled the `typeof opt === 'string' ? opt : opt.value` dance for themselves, and one of them read
 * `opt.value` without the `?.` the others had. `shared/` rather than `app/utils/` because one of those
 * readers (`shared/entities/partyValues.ts`) is itself shared and cannot import from `app/`.
 * Guard: `tests/unit/workflowFieldHelpersGuard.test.ts`.
 */

/** A `{ value, label }` option, as authored. `label` may be absent. */
export interface FieldOptionObject {
  value?: unknown
  label?: unknown
}

/** An option's stored value: the string itself, or its `value`. */
export function optionValue(opt: unknown): any {
  return typeof opt === 'string' ? opt : (opt as FieldOptionObject | null | undefined)?.value
}

/** What an option shows: the string itself, or its `label` (possibly undefined). */
export function optionLabel(opt: unknown): any {
  return typeof opt === 'string' ? opt : (opt as FieldOptionObject | null | undefined)?.label
}

/**
 * The label to SHOW for a stored value — the option's label for a select, the raw value otherwise.
 *
 * A select holds its option value (`soltero/a`), which is the workflow's own token and not what the
 * dropdown renders; showing it verbatim would make a summary disagree with the control the user sees.
 * An empty value shows nothing, and an option with no label falls back to the value itself.
 */
export function optionLabelFor(options: readonly unknown[] | null | undefined, raw: unknown): string {
  if (raw == null || raw === '') return ''
  if (Array.isArray(options)) {
    for (const opt of options) {
      if (optionValue(opt) === raw) return optionLabel(opt) ?? String(raw)
    }
  }
  return String(raw)
}
